import { type FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// common
import CheckBoxSchema from 'common/SchemaForm/element/checkbox/CheckBoxSchema';
import SwitchSchema from 'common/SchemaForm/element/switch/SwitchSchema';

// screen
import type {
  CurrentSelectedRole,
  RolesToAssign,
  Unit,
  UtilFunctions,
} from 'modules/account/types';
import { findByKey } from 'modules/account/detail/helpers/utils';
import FullCustomersList from 'modules/account/components/customers/FullCustomersList';

interface CustomerHeadingProps {
  shouldActivate: boolean;
  shouldDisable: boolean;
  unitAddress?: Unit;
  handleSelectByLocality: (isChecked: boolean) => void;
}

/**
 * Hiển thị switch để chọn lọc khách hàng theo địa phương
 */
const CustomerHeading: FC<CustomerHeadingProps> = (props) => {
  const { shouldActivate, unitAddress, shouldDisable, handleSelectByLocality } = props;

  return (
    <Box sx={{ marginBottom: '12px' }}>
      <SwitchSchema
        label={<FormattedMessage id="account.selectByDistrictOfCQQL" />}
        labelPlacement="end"
        checked={shouldActivate}
        disabled={shouldDisable}
        onChange={handleSelectByLocality}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
        <Typography>
          <FormattedMessage id="account.stateOrCity" />:{' '}
          {unitAddress ? unitAddress.city : 'Không có dữ liệu'}
        </Typography>
        <Typography>
          <FormattedMessage id="account.ward" />:{' '}
          {unitAddress ? unitAddress.ward : 'Không có dữ liệu'}
        </Typography>
      </Box>
    </Box>
  );
};

interface CustomersForCQQLProps {
  /**
   *  thông tin về đơn vị của người dùng
   */
  unitAddress: Unit | undefined;
  isCQQL: boolean;
  rolesToAssign: RolesToAssign;
  currentSelectedRole: CurrentSelectedRole;
  onSelectCustomersByLocality: (defaultToggle: boolean) => void;
  utilFunctions: UtilFunctions;
  viewOnly?: boolean;
}

const CustomersForCQQL: FC<CustomersForCQQLProps> = (props) => {
  const {
    unitAddress,
    isCQQL,
    rolesToAssign,
    currentSelectedRole,
    onSelectCustomersByLocality,
    utilFunctions,
    viewOnly,
  } = props;

  const {
    rolesAssignment: { forGovAgencyBusiness },
    customersSource,
  } = rolesToAssign;

  const [toggle, setToggle] = useState<boolean>(false);

  /**
   * Kiểm tra xem có vai trò nào được chọn hay không,
   * nếu có thì lấy thông tin của vai trò đó
   * nếu không có vai trò nào được chọn thì trả về false để tiện việc xử lý
   */
  const roleToEdit = useMemo(() => {
    if (!currentSelectedRole) return false;
    return findByKey(forGovAgencyBusiness, currentSelectedRole.role.id);
  }, [currentSelectedRole, forGovAgencyBusiness]);

  const locality = customersSource.filter((c) => c.wardName === unitAddress?.ward);

  const handleSelectByLocality = (isChecked: boolean) => {
    setToggle(isChecked);
    onSelectCustomersByLocality(isChecked);
  };

  useEffect(() => {
    setToggle(() => {
      if (!roleToEdit) return false;
      return roleToEdit.includedClientByBoundary;
    });
  }, [roleToEdit]);

  return (
    <Box sx={{ marginTop: '32px' }}>
      <CustomerHeading
        shouldActivate={toggle}
        shouldDisable={Boolean(viewOnly)}
        handleSelectByLocality={handleSelectByLocality}
        unitAddress={unitAddress}
      />
      <Box>
        {toggle ? (
          locality.length > 0 ? (
            locality.map((c) => (
              <Box key={c.operationCode}>
                <CheckBoxSchema label={c.name} checked={true} disabled />
              </Box>
            ))
          ) : (
            <Typography>
              <FormattedMessage id="account.noCustomerByDistrictDescription" />
            </Typography>
          )
        ) : (
          <FullCustomersList
            customers={customersSource}
            viewOnly={viewOnly}
            isCQQL={isCQQL}
            utilFunctions={utilFunctions}
            rolesToAssign={rolesToAssign}
            currentSelectedRole={currentSelectedRole}
          />
        )}
      </Box>
    </Box>
  );
};

export default CustomersForCQQL;
